import ScButton from "@/src/components/ui-kit/button";
import type { CategoryFilters, CategoryTag } from "@/src/models/category.model";
import { useTheme } from "@/src/styles/theme-provider";
import { CloseCircle } from "iconsax-react-nativejs";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Pressable, Text, View } from "react-native";
import { RadioButton } from "react-native-paper";

type CategoryFilterModalProps = Readonly<{
  visible: boolean;
  onClose: () => void;
  onApply: (filters: CategoryFilters) => void;
}>;

type TagOption = Readonly<{
  value: CategoryTag;
  labelKey: string;
}>;

const TAG_OPTIONS: ReadonlyArray<TagOption> = [
  { value: "wiki", labelKey: "LABEL_WIKI" },
  { value: "events", labelKey: "LABEL_EVENTS" },
  { value: "news", labelKey: "LABEL_NEWS" },
];

const CategoryFilterModal = ({
  visible,
  onClose,
  onApply,
}: CategoryFilterModalProps) => {
  const { t } = useTranslation();
  const { themeVars, varStyle } = useTheme();
  const [selectedTag, setSelectedTag] = useState<CategoryTag | null>(null);

  const handleClearAll = () => setSelectedTag(null);

  const handleApply = () => {
    onApply({ tag: selectedTag });
    onClose();
  };

  if (!visible) return null;

  return (
    <Modal transparent animationType="fade">
      <Pressable
        style={varStyle}
        className="flex-1 justify-center items-center bg-shadow-dark px-6"
        onPress={onClose}
      >
        <Pressable
          className="bg-ash rounded-2xl w-full max-w-sm md:w-auto md:max-w-md"
          onPress={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <View className="flex-row items-center justify-between px-6 py-3">
            <Text className="font-medium text-onyx">
              {t("LABEL_SELECT_FILTERS")}
            </Text>
            <Pressable
              onPress={onClose}
              hitSlop={8}
              className="rounded-full hover:bg-ash-3"
            >
              <CloseCircle
                size={20}
                color={themeVars["--color-primary-default"]}
              />
            </Pressable>
          </View>

          {/* Tag Filter */}
          <View className="gap-2 border-t border-ash-3 px-4 py-3">
            <Text className="font-medium px-2 text-onyx">{t("LABEL_TAG")}</Text>
            <RadioButton.Group
              onValueChange={(tagValue) =>
                setSelectedTag(tagValue as CategoryTag)
              }
              value={selectedTag ?? ""}
            >
              {TAG_OPTIONS.map((tagOption) => (
                <Pressable
                  key={tagOption.value}
                  onPress={() => setSelectedTag(tagOption.value)}
                  className="flex-row items-center rounded-xl px-2 py-1 active:bg-ash-3 web:hover:bg-ash-3 web:cursor-pointer"
                >
                  <RadioButton
                    value={tagOption.value}
                    color={themeVars["--color-primary-default"]}
                  />
                  <Text className="text-slate">{t(tagOption.labelKey)}</Text>
                </Pressable>
              ))}
            </RadioButton.Group>
          </View>

          {/* Actions */}
          <View className="flex-row justify-between items-center border-t border-ash-3 gap-3 px-3 py-3">
            <ScButton
              text="BTN_CLEAR_ALL"
              mode="outlined" 
            />
            <View className="flex-row gap-3">
              <ScButton text="BTN_CANCEL" mode="outlined" onPress={onClose} />
              <ScButton text="BTN_APPLY" onPress={handleApply} />
            </View>
          </View>
        </Pressable>
      </Pressable>
    </Modal>
  );
};

export default CategoryFilterModal;
